import {
  AccessTime,
  CheckCircleOutline,
  ContentCopy,
  Group,
  LocationOn,
  Notes,
  Timer,
  TimerOff,
  WarningAmber,
} from '@mui/icons-material';
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/Button';
import { useUserStore } from '@/configs/store';
import { DeleteModal } from '@/features/log/delete';
import type { WeeklyWorkLog } from '@/types';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { formatDurationRussian } from '@/utils/formatDurationRussian';

import type { CalendarEventWithColor, EventAttendee, EventDetailsModalProps } from '../../types';

import s from './EventDetailsModal.module.scss';

const buttonSx = {
  flex: 1,
  borderRadius: '8px',
  boxShadow: 'none',
};

const URL_REGEX = /https?:\/\/[^\s<>"']+/g;

const DOMPURIFY_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'span'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
};

/** Walk DOM text nodes and wrap URLs in <a> tags (skip nodes already inside <a>) */
const linkifyTextNodes = (container: HTMLElement): void => {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const nodesToReplace: { node: Text; html: string }[] = [];

  let current = walker.nextNode();
  while (current) {
    const textNode = current as Text;
    // Skip text inside <a> elements
    if (textNode.parentElement?.closest('a')) {
      current = walker.nextNode();
      continue;
    }

    const text = textNode.textContent ?? '';
    if (URL_REGEX.test(text)) {
      URL_REGEX.lastIndex = 0;
      const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const html = escaped.replace(URL_REGEX, (url) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      });
      nodesToReplace.push({ node: textNode, html });
    }
    URL_REGEX.lastIndex = 0;
    current = walker.nextNode();
  }

  // Replace text nodes with linkified HTML
  for (const { node, html } of nodesToReplace) {
    const span = document.createElement('span');
    span.innerHTML = html;
    node.parentNode?.replaceChild(span, node);
  }
};

/** Linkify plain text: escape HTML, then wrap URLs in <a> tags */
const linkifyPlainText = (text: string): string => {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const linked = escaped.replace(URL_REGEX, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
  URL_REGEX.lastIndex = 0;

  return DOMPurify.sanitize(linked, DOMPURIFY_CONFIG);
};

/** Prepare description HTML: sanitize, then linkify any bare URLs in text nodes */
const prepareDescriptionHtml = (description: string): string => {
  const sanitized = DOMPurify.sanitize(description, DOMPURIFY_CONFIG);

  const div = document.createElement('div');
  div.innerHTML = sanitized;

  // Force existing links to open in new tab
  div.querySelectorAll('a').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });

  // Linkify bare URLs in text nodes that aren't already inside <a>
  linkifyTextNodes(div);

  return div.innerHTML;
};

const RESPONSE_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  ACCEPTED: { label: 'Подтверждён', className: 'accepted' },
  DECLINED: { label: 'Отклонён', className: 'declined' },
  TENTATIVE: { label: 'Сомневается', className: 'tentative' },
  'NEEDS-ACTION': { label: 'Не ответил', className: 'unknown' },
};

const getStatusConfig = (status: string | null) =>
  (status && RESPONSE_STATUS_CONFIG[status]) || { label: 'Не ответил', className: 'unknown' };

const AttendeeRow: React.FC<{ attendee: EventAttendee }> = ({ attendee }) => {
  const statusConfig = getStatusConfig(attendee.responseStatus);
  const showName = attendee.displayName !== null;

  return (
    <div className={s.event_modal__attendee}>
      <Tooltip title={statusConfig.label} arrow placement="top">
        <span
          className={`${s.event_modal__attendee_status} ${s[`event_modal__attendee_status_${statusConfig.className}`]}`}
        />
      </Tooltip>
      <span className={s.event_modal__attendee_name}>
        {showName ? (
          <Tooltip title={attendee.email} arrow placement="top">
            <span>{attendee.displayName}</span>
          </Tooltip>
        ) : (
          attendee.email
        )}
      </span>
      {attendee.worklogStatus === 'confirmed' ? (
        <Tooltip title="Время залогировано" arrow placement="top">
          <Timer className={s.event_modal__attendee_logged} />
        </Tooltip>
      ) : attendee.worklogStatus === 'maybe' ? (
        <Tooltip title="Время возможно залогировано" arrow placement="top">
          <Timer className={s.event_modal__attendee_maybe_logged} />
        </Tooltip>
      ) : (
        <Tooltip title="Время не залогировано" arrow placement="top">
          <TimerOff className={s.event_modal__attendee_not_logged} />
        </Tooltip>
      )}
    </div>
  );
};

const toWeeklyWorkLog = (event: CalendarEventWithColor): WeeklyWorkLog => ({
  id: event.worklogId ?? '',
  taskKey: event.matchedWorklog?.taskKey ?? '',
  taskTitle: event.summary,
  duration: '',
  durationMinutes: event.matchedWorklog?.duration ?? 0,
  comment: '',
  date: DateTime.fromISO(event.startsAt).toISODate() ?? '',
  createdAt: '',
  start: event.matchedWorklog?.start ?? event.startsAt,
});

const EventDetailsModal: React.FC<EventDetailsModalProps> = observer(
  ({
    open,
    onClose,
    event,
    attendees,
    attendeesLoading,
    onLogTime,
    onEditWorklog,
    onDeleteWorklog,
    readOnly = false,
  }) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const { isAdmin } = useUserStore();

    const handleCopyTaskKey = useCallback(async (taskKey: string) => {
      const { isError } = await copyToClipboard(taskKey);

      if (!isError) {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1500);
      }
    }, []);

    const descriptionHtml = useMemo(() => {
      if (!event?.description) return '';
      return prepareDescriptionHtml(event.description);
    }, [event?.description]);

    if (!event) {
      return null;
    }

    const startDateTime = DateTime.fromISO(event.startsAt);
    const endDateTime = DateTime.fromISO(event.endsAt);

    const formatDate = (dt: DateTime) => {
      return dt.setLocale('ru').toFormat('d MMMM yyyy');
    };

    const formatTime = (dt: DateTime) => {
      return dt.toFormat('HH:mm');
    };

    const isSameDay = startDateTime.hasSame(endDateTime, 'day');

    const diffMinutes = endDateTime.diff(startDateTime, 'minutes').minutes;
    const hours = Math.floor(diffMinutes / 60);
    const minutes = Math.round(diffMinutes % 60);
    const durationParts: string[] = [];
    if (hours > 0) durationParts.push(`${hours} ч`);
    if (minutes > 0) durationParts.push(`${minutes} мин`);
    const durationText = durationParts.join(' ') || '0 мин';

    const getTimeDisplay = () => {
      if (event.isAllDay) {
        if (isSameDay) {
          return formatDate(startDateTime);
        }
        return `${formatDate(startDateTime)} — ${formatDate(endDateTime)}`;
      }

      if (isSameDay) {
        return `${formatDate(startDateTime)}, ${formatTime(startDateTime)} — ${formatTime(endDateTime)}`;
      }

      return `${formatDate(startDateTime)} ${formatTime(startDateTime)} — ${formatDate(endDateTime)} ${formatTime(endDateTime)}`;
    };

    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            className: s.event_modal,
          }}
        >
          {/* Color bar at the top */}
          <div className={s.event_modal__color_bar} style={{ backgroundColor: event.color }} />

          {/* Header */}
          <div className={s.event_modal__header}>
            <h1 className={s.event_modal__title}>{event.summary}</h1>
            <IconButton
              aria-label="close"
              onClick={onClose}
              size="small"
              className={s.event_modal__close_button}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <DialogContent className={s.event_modal__content} sx={{ paddingTop: '12px' }}>
            {/* Date and time */}
            <div className={s.event_modal__row}>
              <AccessTime className={s.event_modal__icon} fontSize="small" />
              <div>
                <p className={s.event_modal__text}>{getTimeDisplay()}</p>
                {!event.isAllDay && (
                  <p className={s.event_modal__caption}>
                    {durationText}
                    {event.matchedWorklog && event.matchedWorklog.duration > diffMinutes && (
                      <span className={s.event_modal__extra_time}>
                        {' '}
                        + {formatDurationRussian(event.matchedWorklog.duration - diffMinutes)}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Attendees */}
            {attendeesLoading ? (
              <div className={s.event_modal__row}>
                <Group className={s.event_modal__icon} fontSize="small" />
                <CircularProgress size={16} thickness={4} />
              </div>
            ) : (
              (attendees ?? []).length > 0 && (
                <div className={s.event_modal__row}>
                  <Group className={s.event_modal__icon} fontSize="small" />
                  <div className={s.event_modal__attendees}>
                    {(attendees ?? []).map((attendee) => (
                      <AttendeeRow key={attendee.email} attendee={attendee} />
                    ))}
                  </div>
                </div>
              )
            )}

            {/* Location */}
            {event.location && (
              <div className={s.event_modal__row}>
                <LocationOn className={s.event_modal__icon} fontSize="small" />
                <p
                  className={`${s.event_modal__text} ${s.event_modal__linkable}`}
                  dangerouslySetInnerHTML={{ __html: linkifyPlainText(event.location) }}
                />
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div className={s.event_modal__row}>
                <Notes className={s.event_modal__icon} fontSize="small" />
                <div
                  className={s.event_modal__description}
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              </div>
            )}

            {/* Worklog status */}
            {event.worklogId && event.matchedWorklog && (
              <div className={s.event_modal__status}>
                <Chip
                  icon={<CheckCircleOutline />}
                  label={
                    event.matchedWorklog.taskKey ? (
                      <span className={s.event_modal__status_label}>
                        Залогировано в{' '}
                        <a
                          href={`https://tracker.yandex.ru/${event.matchedWorklog.taskKey}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={s.event_modal__status_link}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {event.matchedWorklog.taskKey}
                        </a>
                        <Tooltip title={isCopied ? 'Скопировано' : 'Копировать ключ'} arrow>
                          <ContentCopy
                            className={s.event_modal__copy_icon}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyTaskKey(event.matchedWorklog!.taskKey);
                            }}
                          />
                        </Tooltip>
                      </span>
                    ) : (
                      'Время залогировано'
                    )
                  }
                  color="success"
                  variant="outlined"
                  size="small"
                />
              </div>
            )}
            {event.worklogId && !event.matchedWorklog && (
              <div className={s.event_modal__status}>
                <Chip
                  icon={<WarningAmber />}
                  label="Лог удалён в трекере — нужно снять отметку"
                  color="warning"
                  variant="outlined"
                  size="small"
                />
              </div>
            )}
          </DialogContent>

          {!event.isAllDay && !readOnly && (
            <DialogActions sx={{ padding: '0 24px 20px' }}>
              <div className={s.event_modal__buttons}>
                {event.worklogId && !event.matchedWorklog ? (
                  <Button sx={buttonSx} variant="outlined" onClick={() => onDeleteWorklog?.(event)}>
                    <p>Снять отметку</p>
                  </Button>
                ) : event.worklogId ? (
                  <>
                    <Button
                      sx={{
                        ...buttonSx,
                        backgroundColor: 'var(--danger-color)',
                        '&:hover': { backgroundColor: 'var(--danger-hover-color)' },
                      }}
                      variant="contained"
                      onClick={() => setConfirmDelete(true)}
                    >
                      <p>Удалить лог</p>
                    </Button>
                    <Button
                      sx={buttonSx}
                      variant="contained"
                      onClick={() => onEditWorklog?.(event)}
                    >
                      <p>Редактировать лог</p>
                    </Button>
                  </>
                ) : isAdmin ? (
                  <>
                    <Button
                      sx={buttonSx}
                      variant="outlined"
                      onClick={() => onLogTime?.(event, false)}
                    >
                      <p>Залогать за себя</p>
                    </Button>
                    <Button
                      sx={buttonSx}
                      variant="contained"
                      onClick={() => onLogTime?.(event, true)}
                    >
                      <p>Залогать за всех</p>
                    </Button>
                  </>
                ) : (
                  <Button
                    sx={buttonSx}
                    variant="contained"
                    onClick={() => onLogTime?.(event, false)}
                  >
                    <p>Залогать время</p>
                  </Button>
                )}
              </div>
            </DialogActions>
          )}
        </Dialog>

        {confirmDelete && (
          <DeleteModal
            logToDelete={toWeeklyWorkLog(event)}
            isLoading={isDeleting}
            cancelDelete={() => setConfirmDelete(false)}
            confirmDelete={async () => {
              setIsDeleting(true);
              try {
                await onDeleteWorklog?.(event);
              } finally {
                setIsDeleting(false);
                setConfirmDelete(false);
                onClose();
              }
            }}
          />
        )}
      </>
    );
  },
);

export { EventDetailsModal };
